import { getSessionStorageOrDefault } from "./hooks/useSessionStorage";

/**
 * Query params that index.html moves out of the URL into sessionStorage on page load (before
 * analytics initialize) so their values are not leaked to 3rd-party analytics.
 *
 * This is the single source of truth for that list. index.html has its own inline copy of the
 * array (it runs before any module loads and cannot import), so if you add a param here you MUST
 * also add it to the `forEach` list in index.html AND ensure every reader of that param falls
 * back to sessionStorage via {@link peekMovedParam} / {@link consumeMovedParam}. A param that is
 * stripped from the URL but read only from the URL caused INC-18719.
 */
export const MOVED_QUERY_PARAM_KEYS = ["redirect_to", "token"] as const;

export type MovedQueryParamKey = (typeof MOVED_QUERY_PARAM_KEYS)[number];

/**
 * sessionStorage key holding the list of params that were stripped from the URL on the CURRENT
 * full-page load. index.html resets it once at the top of every load, and
 * {@link moveQueryParamToSessionStorage} appends to it whenever it stores a value — so a consumer can
 * tell a value that was just stripped this navigation from a leftover one lingering from a prior
 * navigation. This is what prevents a stale invite token from attaching to an unrelated same-tab
 * login (CLRF-2268 M1). It is itself a moved-load marker, not a user value, so it is never a URL param.
 */
export const MOVED_THIS_LOAD_KEY = "moved_query_params_this_load";

/**
 * Move a single query param's value out of the current URL and into sessionStorage, stripping it
 * from the address bar via history.replaceState.
 *
 * This is the canonical implementation of the logic that runs inline in index.html before React
 * loads (index.html cannot import, so it has a hand-copy — keep the two in sync). It is exported
 * here primarily so the write side can be unit-tested against the readers below, closing the
 * encode/decode seam that went untested when INC-18719 shipped.
 *
 * Note: URLSearchParams.get() already percent-decodes once, so the value is stored JSON-encoded
 * WITHOUT a second decode. Readers JSON.parse it; the redirect_to reader decodeURIComponent()s at
 * read time (its stored value is thus still URL-encoded, matching pre-existing behavior).
 */
export const moveQueryParamToSessionStorage = (queryParamKey: string): void => {
  try {
    const pageUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams(pageUrl.search);
    if (!searchParams.has(queryParamKey)) {
      return;
    }

    const queryParamValue = searchParams.get(queryParamKey);
    if (queryParamValue != null && queryParamValue !== "") {
      sessionStorage.setItem(queryParamKey, JSON.stringify(queryParamValue));
      // Record that this param was freshly stripped on THIS page load (same try-block as the store,
      // so the value and its freshness marker can't diverge). Callers reset MOVED_THIS_LOAD_KEY once
      // per load before calling this. See MOVED_THIS_LOAD_KEY / wasMovedThisLoad (CLRF-2268 M1).
      const rawMoved = sessionStorage.getItem(MOVED_THIS_LOAD_KEY);
      const moved: unknown = rawMoved ? JSON.parse(rawMoved) : [];
      const movedList = Array.isArray(moved) ? (moved as string[]) : [];
      if (!movedList.includes(queryParamKey)) {
        movedList.push(queryParamKey);
        sessionStorage.setItem(MOVED_THIS_LOAD_KEY, JSON.stringify(movedList));
      }
    }

    searchParams.delete(queryParamKey);
    pageUrl.search = searchParams.toString();
    window.history.replaceState(null, "", pageUrl);
  } catch (e) {
    console.error(e);
  }
};

/**
 * True if `key` was stripped from the URL on the CURRENT full-page load (per {@link MOVED_THIS_LOAD_KEY}).
 * Consumers of a moved value that could otherwise pick up a leftover (e.g. the invite-token consumer in
 * usePrepareLogin) gate on this so a value from a PRIOR navigation is not treated as belonging
 * to this login. Returns false on missing/invalid marker. (CLRF-2268 M1)
 */
export const wasMovedThisLoad = (key: MovedQueryParamKey): boolean => {
  try {
    const raw = sessionStorage.getItem(MOVED_THIS_LOAD_KEY);
    if (!raw) {
      return false;
    }
    const moved: unknown = JSON.parse(raw);
    return Array.isArray(moved) && moved.includes(key);
  } catch {
    return false;
  }
};

/**
 * Write a moved param's value into sessionStorage using the same JSON-encoded storage format that
 * {@link moveQueryParamToSessionStorage} uses, so {@link peekMovedParam} / {@link consumeMovedParam}
 * read it back correctly.
 *
 * Used to hand a value to the reseller identifier page for a retry — e.g. after a failed
 * teammate-accept, `tryRedirectingUser` writes the (still server-valid) invite token back here so the
 * rerouted page can peek it and let the user resubmit. Empty/whitespace values are ignored (matching
 * the move fn), so a blank token never overwrites a real one. (CLRF-2268)
 */
export const setMovedParam = (key: MovedQueryParamKey, value: string): void => {
  if (value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }
};

/**
 * Read a moved param's value from sessionStorage WITHOUT removing it. index.html stores the value
 * JSON-encoded; this decodes it safely (returns "" on missing/invalid JSON).
 *
 * Use this from render paths that may run multiple times (e.g. a page component that re-renders on
 * input) and that hand the token off to a later consumer. Callers that own the value's lifecycle
 * and want single-use semantics should use {@link consumeMovedParam} instead.
 */
export const peekMovedParam = (key: MovedQueryParamKey): string => {
  // Coerce to string: getSessionStorageOrDefault casts the JSON.parse result to its type param, so a
  // value written by anything other than moveQueryParamToSessionStorage (e.g. a stored number) could
  // otherwise leak a non-string through the `string` return type.
  const value = getSessionStorageOrDefault<unknown>(key, "");
  return typeof value === "string" ? value : "";
};

/**
 * Read a moved param's value from sessionStorage and remove it (single-use). Returns "" when
 * absent. Consuming prevents a stale value (e.g. an invite token) from leaking into a later,
 * unrelated flow in the same tab.
 */
export const consumeMovedParam = (key: MovedQueryParamKey): string => {
  const value = peekMovedParam(key);
  if (value) {
    sessionStorage.removeItem(key);
  }
  return value;
};
